import { Button } from '@suscri/ui';
import i18n from 'i18next';
import { Download, FileSpreadsheet, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

import { dateLocale } from '../lib/i18n';
import { useToast } from '../lib/toast';
import { trpc } from '../lib/trpc';

import { usePremiumAction } from './PremiumAction';

// Importación masiva de suscripciones desde CSV/Excel (acción premium
// BULK_IMPORT, 50 créditos). Autodetecta columnas por nombre de cabecera
// (español/inglés) y muestra una vista previa antes de confirmar.

type ParsedRow = {
  name: string;
  providerName?: string;
  categoryName?: string;
  amount: number;
  currency: string;
  cycle: 'MONTHLY' | 'YEARLY' | 'CUSTOM';
  startDate: string; // ISO, para mostrar y enviar
  nextRenewal: string;
  notes?: string;
  error?: string;
};

const HEADER_ALIASES: Record<string, string[]> = {
  name: ['nombre', 'name', 'suscripcion', 'suscripción', 'servicio'],
  providerName: ['proveedor', 'provider', 'empresa'],
  categoryName: ['categoria', 'categoría', 'category'],
  amount: ['importe', 'amount', 'precio', 'coste', 'cost'],
  currency: ['moneda', 'currency'],
  cycle: ['ciclo', 'cycle', 'periodicidad', 'frecuencia'],
  startDate: ['fecha inicio', 'fecha de inicio', 'start date', 'inicio'],
  nextRenewal: [
    'proxima renovacion',
    'próxima renovación',
    'next renewal',
    'renovacion',
    'renovación',
    'fecha renovacion',
  ],
  notes: ['notas', 'notes', 'observaciones'],
};

function normalizeHeader(h: string): string {
  return h.toString().trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); // quita acentos
}

function detectColumnMap(headers: string[]): Record<string, number> {
  const map: Record<string, number> = {};
  const normalized = headers.map(normalizeHeader);
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    const normalizedAliases = aliases.map(normalizeHeader);
    const idx = normalized.findIndex(h => normalizedAliases.includes(h));
    if (idx !== -1) map[field] = idx;
  }
  return map;
}

function normalizeCycle(raw: unknown): 'MONTHLY' | 'YEARLY' | 'CUSTOM' {
  const s = normalizeHeader(String(raw ?? ''));
  if (s.includes('anual') || s.includes('year')) return 'YEARLY';
  if (s.includes('mes') || s.includes('month')) return 'MONTHLY';
  return 'MONTHLY'; // fallback sensato si no se reconoce
}

function parseDate(raw: unknown): string | null {
  if (raw instanceof Date && !isNaN(raw.getTime())) return raw.toISOString();
  const s = String(raw ?? '').trim();
  if (!s) return null;
  // DD/MM/YYYY
  const dmy = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return isNaN(d.getTime()) ? null : d.toISOString();
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function parseFile(rows: unknown[][]): ParsedRow[] {
  if (rows.length < 2) return [];
  const headers = (rows[0] as string[]).map(String);
  const map = detectColumnMap(headers);
  const dataRows = rows
    .slice(1)
    .filter(r => r.some(cell => cell !== undefined && cell !== ''));

  return dataRows.map(row => {
    const get = (field: string) =>
      map[field] !== undefined ? row[map[field]] : undefined;

    const name = String(get('name') ?? '').trim();
    const amountRaw = get('amount');
    const amount = Number(String(amountRaw ?? '').replace(',', '.'));
    const startDate = parseDate(get('startDate'));
    const nextRenewal = parseDate(get('nextRenewal'));

    let error: string | undefined;
    if (!name) error = i18n.t('Falta el nombre');
    else if (!amountRaw || isNaN(amount) || amount <= 0)
      error = i18n.t('Importe inválido');
    else if (!startDate) error = i18n.t('Fecha de inicio inválida o ausente');
    else if (!nextRenewal)
      error = i18n.t('Próxima renovación inválida o ausente');

    return {
      name,
      providerName: get('providerName')
        ? String(get('providerName'))
        : undefined,
      categoryName: get('categoryName')
        ? String(get('categoryName'))
        : undefined,
      amount: isNaN(amount) ? 0 : amount,
      currency: get('currency') ? String(get('currency')).toUpperCase() : 'EUR',
      cycle: normalizeCycle(get('cycle')),
      startDate: startDate ?? '',
      nextRenewal: nextRenewal ?? '',
      notes: get('notes') ? String(get('notes')) : undefined,
      error,
    };
  });
}

function downloadTemplate() {
  const template = [
    [
      'nombre',
      'proveedor',
      'categoria',
      'importe',
      'moneda',
      'ciclo',
      'fecha inicio',
      'proxima renovacion',
      'notas',
    ],
    [
      'Netflix',
      'Netflix Inc.',
      'Streaming',
      '15.99',
      'EUR',
      'mensual',
      '01/01/2026',
      '01/08/2026',
      '',
    ],
    [
      'Adobe Creative Cloud',
      'Adobe',
      'Software',
      '59.99',
      'EUR',
      'anual',
      '15/03/2026',
      '15/03/2027',
      'Plan empresa',
    ],
  ];
  const csv = template.map(r => r.join(';')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla-suscripciones.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function ImportSubscriptionsModal({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: () => void;
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const toast = useToast();
  const { runMutation, modalElement, actions } = usePremiumAction();
  const bulkImport = trpc.subscriptions.bulkImport.useMutation();
  const importCost = actions?.find(a => a.code === 'BULK_IMPORT')?.cost;

  const validRows = rows.filter(r => !r.error);
  const invalidRows = rows.filter(r => r.error);

  const handleFile = (file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        defval: '',
      });
      setRows(parseFile(raw));
    };
    reader.readAsBinaryString(file);
  };

  const confirmImport = () => {
    if (validRows.length === 0) return;
    runMutation(
      'BULK_IMPORT',
      () =>
        bulkImport.mutateAsync({
          rows: validRows.map(r => ({
            name: r.name,
            providerName: r.providerName,
            categoryName: r.categoryName,
            amount: r.amount,
            currency: r.currency,
            cycle: r.cycle,
            startDate: new Date(r.startDate),
            nextRenewal: new Date(r.nextRenewal),
            notes: r.notes,
          })),
        }),
      result => {
        toast.success(
          t('Importadas {{created}} de {{total}} suscripciones', {
            created: result.created,
            total: result.total,
          }) +
            (result.errors.length
              ? t(' ({{count}} con errores)', { count: result.errors.length })
              : '')
        );
        onImported();
        onClose();
      }
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <FileSpreadsheet className="h-5 w-5 text-violet-600" />
            {t('Importar suscripciones')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          {t(
            'Sube un archivo CSV o Excel con tus suscripciones. Reconocemos automáticamente columnas como "nombre", "importe", "ciclo" o "próxima renovación".'
          )}{' '}
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center gap-1 font-medium text-violet-700 underline"
          >
            <Download className="h-3.5 w-3.5" /> {t('Descargar plantilla')}
          </button>
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />

        {!fileName ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-gray-300 py-10 text-gray-500 hover:border-violet-400 hover:text-violet-600 dark:border-gray-700"
          >
            <Upload className="h-8 w-8" />
            <span className="text-sm font-medium">
              {t('Haz clic para seleccionar un archivo')}
            </span>
          </button>
        ) : (
          <div>
            <div className="mb-3 flex items-center justify-between text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {fileName}
              </span>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-violet-700 underline"
              >
                {t('Cambiar archivo')}
              </button>
            </div>

            {rows.length === 0 ? (
              <p className="text-sm text-red-600">
                {t('No se detectaron filas válidas en el archivo.')}
              </p>
            ) : (
              <>
                <div className="mb-3 flex gap-4 text-sm">
                  <span className="font-semibold text-emerald-700">
                    {t('{{count}} listas para importar', {
                      count: validRows.length,
                    })}
                  </span>
                  {invalidRows.length > 0 && (
                    <span className="font-semibold text-red-600">
                      {t('{{count}} con errores (se omitirán)', {
                        count: invalidRows.length,
                      })}
                    </span>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-left text-xs">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-slate-800">
                      <tr>
                        <th className="px-3 py-2">{t('Nombre')}</th>
                        <th className="px-3 py-2">{t('Importe')}</th>
                        <th className="px-3 py-2">{t('Ciclo')}</th>
                        <th className="px-3 py-2">{t('Renovación')}</th>
                        <th className="px-3 py-2">{t('Estado')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {rows.slice(0, 20).map((r, i) => (
                        <tr
                          key={i}
                          className={
                            r.error ? 'bg-red-50 dark:bg-red-950/30' : ''
                          }
                        >
                          <td className="px-3 py-2">{r.name || '—'}</td>
                          <td className="px-3 py-2">
                            {r.amount ? `${r.amount} ${r.currency}` : '—'}
                          </td>
                          <td className="px-3 py-2">{r.cycle}</td>
                          <td className="px-3 py-2">
                            {r.nextRenewal
                              ? new Date(r.nextRenewal).toLocaleDateString(
                                  dateLocale()
                                )
                              : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {r.error ? (
                              <span className="text-red-600">{r.error}</span>
                            ) : (
                              <span className="text-emerald-700">OK</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {rows.length > 20 && (
                  <p className="mt-2 text-xs text-gray-500">
                    {t('Mostrando 20 de {{total}} filas.', {
                      total: rows.length,
                    })}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {t('Cancelar')}
          </Button>
          <Button
            onClick={confirmImport}
            disabled={validRows.length === 0 || bulkImport.isPending}
          >
            {bulkImport.isPending
              ? t('Importando…')
              : t('Importar {{count}} suscripciones', {
                  count: validRows.length,
                }) + (importCost ? ` · ${importCost} cr.` : '')}
          </Button>
        </div>
      </div>
      {modalElement}
    </div>
  );
}
